import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { EmptyState } from "@/components/DashboardShell";
import ActionPanel from "@/components/ActionPanel";
import { ShoppingCart, Truck, ClipboardText, Storefront } from "@phosphor-icons/react";
import { formatPrice, UNITS_LABEL } from "@/lib/format";

/**
 * CompradorPedidosPage — real workflow.
 * The order lifecycle isn't yet a first-class object, but every meaningful
 * pre-order state IS already tracked: (a) offers I've received, (b) sellers I've
 * unlocked and (c) my open requests. This page brings them together as an
 * actionable "orders in progress" view.
 */
export function CompradorPedidosPage() {
    const [received, setReceived] = useState({ items: [], contact_locked: true });
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [o, c] = await Promise.all([
                    api.get("/my/offers-received"),
                    api.get("/my/contacts-unlocked"),
                ]);
                setReceived(o.data || { items: [] });
                setContacts(c.data?.items || []);
            } catch { /* noop */ }
            finally { setLoading(false); }
        })();
    }, []);

    const isEmpty = !received.items?.length && !contacts.length;

    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Pedidos</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Pedidos en curso</h1>
                <p className="text-muted-foreground mt-1">Ofertas recibidas de vendedores y vendedores con contacto activo.</p>
            </div>

            {loading ? (
                <div className="text-center text-muted-foreground py-20" data-testid="pedidos-loading">Cargando…</div>
            ) : isEmpty ? (
                <EmptyState
                    title="Aún no tiene pedidos en curso"
                    hint="Cuando reciba una oferta o contacte a un vendedor, aparecerá aquí como un pedido en curso."
                    cta="Explorar marketplace"
                    ctaTo="/marketplace"
                    icon={ShoppingCart}
                    testId="comprador-pedidos-empty"
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <section>
                        <h2 className="section-subtitle mb-3">Ofertas recibidas</h2>
                        {received.items?.length ? (
                            <ul className="bg-white border border-border rounded-2xl divide-y divide-border" data-testid="pedidos-ofertas-list">
                                {received.items.slice(0, 8).map((o) => (
                                    <li key={o.id} className="p-4">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <span className="font-semibold truncate">{o.seller?.business_name || o.seller?.name || "Vendedor"}</span>
                                            <Link to="/comprador/ofertas" className="text-xs font-bold text-[#2E7D32]">Ver oferta →</Link>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {o.quantity} {UNITS_LABEL[o.unit] || o.unit || ""} a {formatPrice(o.price_per_unit || 0)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground">Sin ofertas por ahora.</p>
                        )}
                    </section>
                    <section>
                        <h2 className="section-subtitle mb-3">Vendedores contactados</h2>
                        {contacts.length ? (
                            <ul className="bg-white border border-border rounded-2xl divide-y divide-border">
                                {contacts.slice(0, 8).map((c) => (
                                    <li key={c.id || c.seller_id} className="p-4">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-semibold truncate">{c.seller?.business_name || c.seller?.name || "Vendedor"}</span>
                                            <Link to="/comprador/compras" className="text-xs font-bold text-[#2E7D32]">Ver →</Link>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{c.seller?.city}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-muted-foreground">Aún no ha contactado a vendedores.</p>
                        )}
                    </section>
                </div>
            )}
        </>
    );
}

export function CompradorComprasPage() {
    // Preserved for backwards-import compatibility — full implementation lives
    // in the dedicated CompradorComprasPage.jsx file.
    return null;
}

export function CompradorEnviosPage() {
    return (
        <>
            <div className="mb-6">
                <div className="eyebrow mb-1">Envíos</div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Coordinar envío</h1>
                <p className="text-muted-foreground mt-1">Encuentre transportistas verificados para su carga.</p>
            </div>
            <ActionPanel
                title="Directorio de transportistas verificados"
                body="Contacte directamente a transportistas de todo México. Filtre por estado, capacidad y tipo de unidad (torton, rabon, jaula ganadera, refrigerado)."
                testId="comprador-envios-action"
                actions={[
                    { label: "Ver transportistas", to: "/directorio/transportista", primary: true, icon: Truck, testId: "comprador-envios-directory" },
                    { label: "Publicar carga como solicitud", to: "/solicitudes", icon: ClipboardText },
                ]}
            />
        </>
    );
}

export function CompradorGastosPage() {
    // Legacy re-export — the real implementation lives in CompradorGastosPage.jsx.
    return null;
}
